import { Box, Flex, IconButton, Text } from '@chakra-ui/react';
import { ChevronDownIcon, ChevronUpIcon, LockIcon, ViewIcon, ViewOffIcon } from '@chakra-ui/icons';
import React, { useCallback, useEffect, useState } from 'react';
import { throttle } from 'lodash';
import { useCustomerDisplayState } from 'helpers/customerDisplayChannel';
import { CustomerDisplayScreenContent, TICKET_PAGE_BG, QrPaymentOverlay } from './BookingCustomerDisplay';

// Fixed panel width; content is scaled down from the display's real
// viewportSize to fit. Not shrunk on narrow admin viewports — the operator
// needs to actually read it.
const PREVIEW_WIDTH_PX = 400;

// Safari doesn't apply CSS `zoom` to the injected seat-map SVG, leaving it
// unscaled — see the branch below. Chrome/Firefox handle it fine via zoom.
const isSafari = typeof navigator !== 'undefined' && /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

// Floating, collapsible mirror of the Customer Display, so the operator
// (facing away from it) can see what's on screen. Scrolling this is the
// only way the real display scrolls — it broadcasts a 'scroll-sync' ratio.
export default function CustomerDisplayPreview() {
  const {
    screen,
    sendMessage,
    viewportSize,
    isDisplayConnected,
    isCurtainClosed,
    setCurtainClosed,
  } = useCustomerDisplayState();
  const [isExpanded, setIsExpanded] = useState(true);

  const scale = PREVIEW_WIDTH_PX / viewportSize.width;
  const previewHeightPx = viewportSize.height * scale;

  // Catch up in case the display was already open before this preview's
  // channel existed to hear its mount-time broadcasts.
  useEffect(() => {
    sendMessage({ type: 'request-sync' });
  }, [sendMessage]);

  const handleScroll = useCallback(
    throttle((event) => {
      const el = event.target;
      const max = el.scrollHeight - el.clientHeight;
      const ratio = max > 0 ? el.scrollTop / max : 0;
      sendMessage({ type: 'scroll-sync', ratio });
    }, 120),
    [sendMessage]
  );

  useEffect(() => () => handleScroll.cancel(), [handleScroll]);

  // Nothing real to mirror until a Customer Display window is actually out
  // there and has answered the handshake above — showing this beforehand
  // would just be an idle screen nobody's looking at.
  if (!isDisplayConnected) return null;

  return (
    <Box
      position='fixed'
      right='24px'
      top='64px'
      maxW='95vw'
      zIndex={1300}
      bg='white'
      borderRadius='md'
      boxShadow='0 8px 24px rgba(0,0,0,0.25)'
      border='1px solid #E2E8F0'
      overflow='hidden'
    >
      <Flex
        align='center'
        justify='space-between'
        gap={3}
        px={3}
        py={2}
        bg='gray.50'
        borderBottom={isExpanded ? '1px solid #E2E8F0' : 'none'}
        cursor='pointer'
        onClick={() => setIsExpanded((v) => !v)}
      >
        <Flex align='center' gap={2}>
          <Box boxSize='8px' borderRadius='full' bg={isCurtainClosed ? 'gray.400' : 'green.400'} flexShrink={0} />
          <Text fontSize='xs' fontWeight='bold' color='gray.600' whiteSpace='nowrap'>
            CUSTOMER DISPLAY{isCurtainClosed ? ' · HIDDEN' : ''}
          </Text>
        </Flex>
        <Flex align='center' gap={1}>
          <IconButton
            aria-label={isCurtainClosed ? 'Open curtain' : 'Close curtain'}
            icon={isCurtainClosed ? <ViewOffIcon /> : <ViewIcon />}
            size='xs'
            variant='ghost'
            colorScheme='gray'
            onClick={(event) => {
              event.stopPropagation();
              setCurtainClosed(!isCurtainClosed);
            }}
          />
          <IconButton
            aria-label={isExpanded ? 'Collapse preview' : 'Expand preview'}
            icon={isExpanded ? <ChevronDownIcon /> : <ChevronUpIcon />}
            size='xs'
            variant='ghost'
          />
        </Flex>
      </Flex>
      {isExpanded && (
        <Box position='relative'>
          <Box
            w={`${PREVIEW_WIDTH_PX}px`}
            maxW='95vw'
            h={`${previewHeightPx}px`}
            bg={screen.type === 'confirmation' ? TICKET_PAGE_BG : 'white'}
            overflowY={isCurtainClosed ? 'hidden' : 'auto'}
            overflowX='hidden'
            onScroll={isCurtainClosed ? undefined : handleScroll}
          >
            {isSafari ? (
              // Fallback: `transform` scales the SVG correctly (unlike `zoom`),
              // but creates a new containing block for position:sticky, so
              // `isPreview` drops the header back to normal flow instead.
              <Box w={`${PREVIEW_WIDTH_PX}px`} maxW='95vw' h={`${previewHeightPx}px`} position='relative'>
                <Box
                  position='absolute'
                  top={0}
                  left={0}
                  w={`${viewportSize.width}px`}
                  h={`${viewportSize.height}px`}
                  transform={`scale(${scale})`}
                  transformOrigin='top left'
                >
                  <CustomerDisplayScreenContent screen={screen} isPreview />
                </Box>
              </Box>
            ) : (
              // `zoom` resizes at layout time (unlike `transform`), so
              // scrollHeight and position:sticky work without a separate
              // isPreview fallback. `h` matters too: IdleScreen's `h='100%'`
              // needs a parent with a definite height, or it renders
              // uncentered at the top.
              <Box w={`${viewportSize.width}px`} h={`${viewportSize.height}px`} sx={{ zoom: scale }}>
                <CustomerDisplayScreenContent screen={screen} />
              </Box>
            )}
          </Box>
          {/* Sibling of the scrollable box, not nested in it, so it stays
              pinned regardless of scroll. 'absolute' (not 'fixed') scopes it
              to this panel's wrapping `position: relative` Box. */}
          <QrPaymentOverlay qrPayment={screen.data?.qrPayment} position='absolute' scale={scale} />
          {isCurtainClosed && (
            <Flex
              position='absolute'
              inset={0}
              align='center'
              justify='center'
              bg='blackAlpha.100'
              backdropFilter='blur(2px)'
              pointerEvents='none'
            >
              <LockIcon boxSize='28px' color='gray.400' opacity={0.6} />
            </Flex>
          )}
        </Box>
      )}
    </Box>
  );
}
